import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import axiosConfig from "restApi/axiosConfig";
import { getPushStatus, enablePushNotifications } from "utils/webPush";
import { NOTIFICATION_PROMPT_DISMISSED_KEY } from "CONSTANTS";

// Shown once by EventDetailPage.jsx right after a leader publishes an
// event (their own status draft -> published transition) - a natural,
// low-friction moment to suggest turning notifications on, rather than
// an unprompted banner on first load. Self-closes with no dialog at all
// (calls onClose immediately) whenever it isn't actually eligible: push
// unsupported, permission already denied, already subscribed, or the
// user previously picked "Don't ask again" here. A plain "Not now"
// leaves that flag unset, so this can still resurface the next time
// they publish - only an explicit "Don't ask again" permanently
// suppresses it.
export default ({ onClose }) => {
  const [open, setOpen] = React.useState(false);
  const [vapidPublicKey, setVapidPublicKey] = React.useState("");
  const [enabling, setEnabling] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (localStorage.getItem(NOTIFICATION_PROMPT_DISMISSED_KEY)) {
      onClose();
      return;
    }
    Promise.all([getPushStatus(), axiosConfig.get("/api/config/")]).then(
      ([pushStatus, res]) => {
        if (cancelled) return;
        if (pushStatus !== "not-subscribed") {
          // subscribed: nothing to ask. unsupported/denied: asking
          // again would either do nothing or can't succeed - denied
          // specifically can only be undone from the browser's own site
          // settings, not by re-prompting here.
          onClose();
          return;
        }
        setVapidPublicKey(res.data.vapidPublicKey || "");
        setOpen(true);
      }
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => {
    setOpen(false);
    onClose();
  };

  const handleEnable = () => {
    setEnabling(true);
    enablePushNotifications(vapidPublicKey)
      .catch(() => {})
      .finally(close);
  };

  const handleDontAskAgain = () => {
    localStorage.setItem(NOTIFICATION_PROMPT_DISMISSED_KEY, "1");
    close();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={close}>
      <DialogTitle>Get notified about events?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Turn on notifications to hear about new or updated events - even
          when the app is closed. You can change this anytime in Account
          Settings.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDontAskAgain}>Don't ask again</Button>
        <Button onClick={close}>Not now</Button>
        <Button color="secondary" variant="contained" disabled={enabling} onClick={handleEnable}>
          Enable
        </Button>
      </DialogActions>
    </Dialog>
  );
};
